import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { GroupAdd, Add, Close, FileCopy, Person, Save } from "@material-ui/icons";

import select from "../../../../utils/select";
import { filterCouncil } from "../../../../reducer/council/action";
import { notification } from "../../../../reducer/home/action";
import {
  createSelectionCouncil,
  createMidtermCouncil,
  createFinalCouncil,
  getCouncilHistory,
} from "../../../../reducer/internalResearch/action";
import {
  COUNCIL_ROLES,
  buildDefaultMembers,
  groupMembersByRole,
  getRoleConfig,
  validateMembers,
} from "../../../../constants/councilRoles";
import {
  INT_COUNCIL_TYPE_SELECTION,
  INT_COUNCIL_TYPE_MIDTERM,
  INT_COUNCIL_TYPE_FINAL,
  INT_COUNCIL_ASSIGNMENT_STATUS_LABEL,
} from "../../../../constants/internalResearchStatus";
import SectionCard from "../../utils/SectionCard";

// CouncilForm — gán 1 trong 3 hội đồng cho đề tài Cơ sở (selection/midterm/final).
// Dispatch tới action tương ứng theo `councilType`. Hỗ trợ template (auto-fill
// members snapshot, vẫn cho edit từng người sau khi áp dụng).
const COUNCIL_TYPE_LABELS = {
  [INT_COUNCIL_TYPE_SELECTION]: "Hội đồng tuyển chọn (HĐ#1)",
  [INT_COUNCIL_TYPE_MIDTERM]:   "Hội đồng đánh giá tiến độ (HĐ#2)",
  [INT_COUNCIL_TYPE_FINAL]:     "Hội đồng nghiệm thu (HĐ#3)",
};

const DISPATCH_FN_BY_TYPE = {
  [INT_COUNCIL_TYPE_SELECTION]: createSelectionCouncil,
  [INT_COUNCIL_TYPE_MIDTERM]:   createMidtermCouncil,
  [INT_COUNCIL_TYPE_FINAL]:     createFinalCouncil,
};

export default function CouncilForm({ detail, councilType, onDone }) {
  const dispatch = useDispatch();
  const profileListRaw = useSelector((s) => select(s, "profileReducer", "list")) || [];
  const templateList = useSelector((s) => select(s, "councilReducer", "council")) || [];

  // Refactor 2026-05-21: loại cán bộ tham gia đề tài khỏi danh sách chọn HĐ —
  // tránh xung đột lợi ích (CN/thành viên đề tài ngồi HĐ chấm chính đề tài
  // mình tham gia).
  const profileList = useMemo(() => {
    const excludeIds = new Set(
      (detail?.participants || [])
        .map((p) => p?.profileId)
        .filter((id) => id && id > 0)
    );
    if (excludeIds.size === 0) return profileListRaw;
    return profileListRaw.filter((p) => !excludeIds.has(p.id));
  }, [profileListRaw, detail?.participants]);

  const existing = (detail?.councilAssignments || []).find((a) => a.councilType === councilType);

  const [values, setValues] = useState({
    councilTemplateId: existing?.councilTemplateId || 0,
    members: existing?.members?.length ? existing.members : buildDefaultMembers(),
  });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    // Refactor 2026-05-16: pool template Cơ sở tách riêng khỏi SV (module="internal").
    // Filter theo cả councilType (selection/midterm/final) để admin chỉ thấy template
    // phù hợp với cấp HĐ đang gán.
    dispatch(filterCouncil({
      schoolYear: detail?.schoolYear,
      councilType,
      module: "internal",
    }));
  }, [dispatch, detail?.schoolYear, councilType]);

  // Các lần hội đồng trước đó (đã bị thay thế) — cho admin "lấy từ lịch sử"
  // để điền lại thành viên của 1 lần cũ, thay vì chỉ pre-fill được lần gần
  // nhất (existing, vẫn còn tồn tại cho tới khi bấm "Lưu hội đồng").
  useEffect(() => {
    let cancelled = false;
    if (!detail?.id) return undefined;
    getCouncilHistory(detail.id, councilType).then((rows) => {
      if (!cancelled) setHistory(rows || []);
    });
    return () => {
      cancelled = true;
    };
  }, [detail?.id, councilType]);

  const applyHistory = (entry) => {
    if (!entry) return;
    setValues({
      councilTemplateId: 0,
      members: (entry.members || []).map((m) => ({ ...m })),
    });
    dispatch(notification({ type: "info", message: `Đã lấy thành viên từ hội đồng lần ${entry.round}.` }));
  };

  const grouped = useMemo(() => groupMembersByRole(values.members), [values.members]);
  const setMembers = (next) => setValues((v) => ({ ...v, members: next }));

  const applyTemplate = (template) => {
    if (!template) {
      setValues({ councilTemplateId: 0, members: buildDefaultMembers() });
      return;
    }
    setValues({
      councilTemplateId: template.id,
      members: (template.members || []).map((m) => ({ ...m })),
    });
    dispatch(notification({ type: "info", message: `Đã áp dụng template "${template.name}".` }));
  };

  const addMember = (roleKey) => {
    const cfg = getRoleConfig(roleKey);
    const same = values.members.filter((m) => m.roleKey === roleKey);
    if (same.length >= cfg.max) {
      dispatch(notification({ type: "warning", message: `Tối đa ${cfg.max} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    const nextOrder = (same[same.length - 1]?.orderNum || 0) + 1;
    setMembers([
      ...values.members,
      { roleKey, orderNum: nextOrder, profileId: 0, name: "", degree: "", isPhenikaa: true },
    ]);
  };
  const removeMember = (roleKey, orderNum) => {
    const cfg = getRoleConfig(roleKey);
    const same = values.members.filter((m) => m.roleKey === roleKey);
    if (same.length <= cfg.min) {
      dispatch(notification({ type: "warning", message: `Cần ít nhất ${cfg.min} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    setMembers(values.members.filter((m) => !(m.roleKey === roleKey && m.orderNum === orderNum)));
  };
  const updateMember = (roleKey, orderNum, patch) => {
    setMembers(values.members.map((m) =>
      m.roleKey === roleKey && m.orderNum === orderNum ? { ...m, ...patch } : m
    ));
  };

  const handleSubmit = async () => {
    const reason = validateMembers(values.members);
    if (reason) {
      dispatch(notification({ type: "warning", message: reason }));
      return;
    }
    const payload = {
      councilType,
      councilTemplateId: values.councilTemplateId || 0,
      members: values.members,
    };
    setSaving(true);
    try {
      const fn = DISPATCH_FN_BY_TYPE[councilType];
      const ok = await dispatch(fn(detail.id, payload));
      if (ok && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard
        icon={<GroupAdd />}
        title={COUNCIL_TYPE_LABELS[councilType] || "Gán hội đồng"}
        marginTop={0}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Áp dụng template tiểu ban</b>
            </Typography>
            <Box display="flex" gridGap={8}>
              <Autocomplete
                options={templateList}
                getOptionLabel={(o) => o?.name || ""}
                value={templateList.find((t) => t.id === values.councilTemplateId) || null}
                onChange={(_, val) => applyTemplate(val)}
                style={{ flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params} size="small" variant="outlined"
                    placeholder={templateList.length === 0 ? "Chưa có template" : "Chọn template..."}
                  />
                )}
              />
              {values.councilTemplateId > 0 && (
                <Tooltip title="Bỏ template (chuyển sang nhập tay)">
                  <IconButton size="small" onClick={() => applyTemplate(null)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {values.councilTemplateId > 0 && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "flex", alignItems: "center", gridGap: 4 }}>
                <FileCopy fontSize="inherit" /> Đang áp dụng template (snapshot lưu khi nhấn "Lưu hội đồng").
              </Typography>
            )}
          </Grid>

          {history.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>Lấy từ lịch sử</b>
                <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                  (điền lại thành viên của 1 lần hội đồng trước đó, vẫn sửa tự do trước khi lưu)
                </Typography>
              </Typography>
              <Select
                fullWidth
                displayEmpty
                variant="outlined"
                value=""
                onChange={(e) => applyHistory(history.find((h) => h.round === e.target.value))}
                renderValue={() => "Chọn 1 lần hội đồng trước..."}
              >
                {history.map((h) => (
                  <MenuItem key={h.round} value={h.round}>
                    {`Lần ${h.round} — ${INT_COUNCIL_ASSIGNMENT_STATUS_LABEL[h.status] || h.status}`}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box border="solid 1px #e0e0e0" borderRadius={4} padding={2} marginTop={1}>
              {COUNCIL_ROLES.map((role) => {
                const list = grouped[role.key] || [];
                return (
                  <Box key={role.key} marginBottom={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                      <Typography variant="body2">
                        <b>{role.label}</b>
                        <span style={{ color: "#9e9e9e", marginLeft: 8, fontSize: 12 }}>
                          ({list.length}{role.max > 1 ? `/${role.max}` : ""})
                        </span>
                      </Typography>
                      {role.max > role.min && (
                        <Button
                          size="small" variant="outlined" color="primary"
                          startIcon={<Add />}
                          disabled={list.length >= role.max}
                          onClick={() => addMember(role.key)}
                        >
                          Thêm {role.label.toLowerCase()}
                        </Button>
                      )}
                    </Box>
                    {list.map((m) => (
                      <MemberRow
                        key={`${m.roleKey}-${m.orderNum}`}
                        member={m}
                        profileList={profileList}
                        canRemove={list.length > role.min}
                        onChange={(patch) => updateMember(m.roleKey, m.orderNum, patch)}
                        onRemove={() => removeMember(m.roleKey, m.orderNum)}
                      />
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </SectionCard>

      <Box marginTop={3} paddingTop={2} display="flex" justifyContent="flex-end" borderTop="solid 1px #e0e0e0">
        <Button
          variant="contained" color="primary"
          startIcon={<Save />} disabled={saving}
          onClick={handleSubmit}
        >
          Lưu hội đồng
        </Button>
      </Box>
    </Box>
  );
}

function MemberRow({ member, profileList, canRemove, onChange, onRemove }) {
  const isPhenikaa = member.isPhenikaa !== false;
  const selectedProfile = isPhenikaa
    ? profileList.find((p) => p.id === member.profileId) || null
    : null;

  return (
    <Box
      display="flex"
      gridGap={8}
      alignItems="center"
      padding="6px 8px"
      bgcolor="#fafafa"
      borderRadius={4}
      marginBottom={6}
      border="solid 1px #eee"
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={isPhenikaa}
            onChange={(e) => onChange({ isPhenikaa: e.target.checked, profileId: 0, name: "", degree: "" })}
          />
        }
        label={<Typography variant="caption">Nội bộ</Typography>}
        style={{ marginLeft: 0, marginRight: 4 }}
      />
      {isPhenikaa ? (
        <Autocomplete
          options={profileList}
          getOptionLabel={(o) => `${o.name || ""}${o.degree ? ` (${o.degree})` : ""}`}
          value={selectedProfile}
          onChange={(_, val) => onChange({ profileId: val?.id || 0, name: val?.name || "", degree: val?.degree || "" })}
          style={{ flexGrow: 1, minWidth: 280 }}
          renderInput={(params) => (
            <TextField {...params} size="small" variant="outlined" placeholder="Tìm cán bộ..." />
          )}
        />
      ) : (
        <>
          <TextField
            size="small" variant="outlined" placeholder="Họ tên"
            value={member.name || ""}
            onChange={(e) => onChange({ name: e.target.value })}
            style={{ flexGrow: 1 }}
          />
          <TextField
            size="small" variant="outlined" placeholder="Học vị"
            value={member.degree || ""}
            onChange={(e) => onChange({ degree: e.target.value })}
            style={{ width: 140 }}
          />
        </>
      )}
      {canRemove && (
        <Tooltip title="Xoá thành viên">
          <IconButton size="small" onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!member.profileId && !member.name && (
        <Tooltip title="Chưa chọn người">
          <Person fontSize="small" style={{ color: "#c62828" }} />
        </Tooltip>
      )}
    </Box>
  );
}
